import api from './client';
import type {
  InvitationCreate,
  InvitationResponse,
  InvitationValidateResponse,
  AcceptInvitationRequest,
  AcceptInvitationResponse,
  SendGuestCodeRequest,
  SendGuestCodeResponse,
  GuestLoginRequest,
  GuestLoginResponse,
} from './types';

export const invitationsApi = {
  /**
   * Create an invitation to join the organization
   * @param invitation - Invitation data
   */
  create: async (invitation: InvitationCreate): Promise<InvitationResponse> => {
    const { data } = await api.post('/invitations', invitation);
    return data;
  },

  /**
   * Validate an invitation without authentication
   * @param token - Web invitation token (optional)
   * @param code - Mobile invitation code (optional)
   */
  validate: async (params: { token?: string; code?: string }): Promise<InvitationValidateResponse> => {
    const { data } = await api.get('/invitations/validate', { params });
    return data;
  },

  /**
   * Accept an invitation and join the organization as a full member
   * @param request - Accept invitation request (token or code)
   */
  accept: async (request: AcceptInvitationRequest): Promise<AcceptInvitationResponse> => {
    const { data } = await api.post('/invitations/accept', request);
    return data;
  },

  /**
   * Send a verification code to a guest for passwordless auth
   * @param request - Send guest code request
   */
  sendGuestCode: async (request: SendGuestCodeRequest): Promise<SendGuestCodeResponse> => {
    const { data } = await api.post('/invitations/send-guest-code', request);
    return data;
  },

  /**
   * Guest authentication - first-time acceptance or returning login
   * @param request - Guest login request
   */
  guestAuth: async (request: GuestLoginRequest): Promise<GuestLoginResponse> => {
    const { data } = await api.post('/invitations/guest-auth', request);
    return data;
  },

  /**
   * List all invitations for the user's organization
   */
  list: async (): Promise<InvitationResponse[]> => {
    const { data } = await api.get('/invitations');
    return data;
  },

  /**
   * Cancel a pending invitation
   * @param invitationId - Invitation ID
   */
  cancel: async (invitationId: string): Promise<{ message: string }> => {
    const { data } = await api.delete(`/invitations/${invitationId}`);
    return data;
  },

  /**
   * Resend invitation email for a user with pending status
   * @param userId - User ID
   */
  resend: async (userId: string): Promise<{ message: string }> => {
    const { data } = await api.post(`/invitations/resend/${userId}`);
    return data;
  },
};
